"use client";

import { useEffect } from "react";

/**
 * Defers loading the Tawk.to chat widget until the user demonstrates intent
 * (first scroll / pointerdown / keydown / touchstart) or 6 s after page load,
 * whichever comes first.
 *
 * Previously the script was loaded via Next.js `Script strategy="lazyOnload"`,
 * which still fires on every page during idle — costing ~100 KB of JS during
 * the post-load window and hurting PageSpeed (TBT, TTI, INP).
 *
 * Pairs with `CustomChatButton`, which polls for `window.Tawk_API` and only
 * renders once the script is ready.
 */
export default function TawkLazyLoader() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Avoid double-injection if React strict-mode mounts twice in dev.
    if ((window as unknown as { __tawkInjected?: boolean }).__tawkInjected) {
      return;
    }

    let injected = false;

    const inject = () => {
      if (injected) return;
      injected = true;
      (window as unknown as { __tawkInjected?: boolean }).__tawkInjected = true;

      // Initialize globals exactly as the official snippet does.
      const w = window as unknown as {
        Tawk_API?: Record<string, unknown> & {
          hideWidget?: () => void;
          onBeforeLoad?: () => void;
          onLoad?: () => void;
        };
        Tawk_LoadStart?: Date;
      };
      w.Tawk_API = w.Tawk_API || {};
      w.Tawk_LoadStart = new Date();
      // Hide the default Tawk.to bubble — we use CustomChatButton instead.
      // onBeforeLoad runs before the bubble renders (no flash); onLoad is
      // the fallback in case the early call is ignored.
      const hideBubble = () => w.Tawk_API?.hideWidget?.();
      w.Tawk_API.onBeforeLoad = hideBubble;
      w.Tawk_API.onLoad = hideBubble;

      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = "https://embed.tawk.to/6abfa43d6d2e4e34c2a93873/1k3u9j4jc";
      s1.charset = "UTF-8";
      s1.setAttribute("crossorigin", "*");
      const s0 = document.getElementsByTagName("script")[0];
      s0?.parentNode?.insertBefore(s1, s0);

      cleanup();
    };

    const events: Array<keyof WindowEventMap> = [
      "scroll",
      "pointerdown",
      "keydown",
      "touchstart",
    ];
    const onInteraction = () => inject();

    const cleanup = () => {
      for (const ev of events) {
        window.removeEventListener(ev, onInteraction);
      }
      window.clearTimeout(idleTimer);
    };

    for (const ev of events) {
      window.addEventListener(ev, onInteraction, { passive: true, once: true });
    }
    // Fallback: load 6 s after mount so chat eventually becomes available
    // for users who never interact (e.g. reading the hero).
    const idleTimer = window.setTimeout(inject, 6000);

    return cleanup;
  }, []);

  return null;
}
